import { useState } from "react";
import Layouts from "../../Layouts";
import { FaEye, FaSync } from "react-icons/fa";
import { GiScalpel } from "react-icons/gi";
import { MdOutlineLocalHospital } from "react-icons/md";
import { TbChecklist, TbClockHour4, TbCircleCheck } from "react-icons/tb";
import { BsFillPlayCircleFill } from "react-icons/bs";

/* ─── Mock Data ─── */
const MOCK_PATIENTS = [
  {
    id: 1,
    name: "Rajesh Kumar",
    patientId: "DZX5641807",
    mobile: "+91 98765 43210",
    ageGender: "45 Y / Male",
    procedure: "Appendectomy",
    otRoom: "OT-01",
    surgeryDateTime: "29/09/2026\n09:00 AM",
    doctor: "Dr. Karthik",
    doctorRole: "General Surgeon",
    status: "Scheduled",
    avatar: "R",
    avatarBg: "#EF4444",
  },
  {
    id: 2,
    name: "Sangeetha R",
    patientId: "DZX5641808",
    mobile: "+91 87654 32109",
    ageGender: "32 Y / Female",
    procedure: "Lap Cholecystectomy",
    otRoom: "OT-02",
    surgeryDateTime: "29/09/2026\n11:30 AM",
    doctor: "Dr. Priya",
    doctorRole: "General Surgeon",
    status: "In Progress",
    avatar: "S",
    avatarBg: "#6366F1",
  },
  {
    id: 3,
    name: "Murugan K",
    patientId: "DZX5641809",
    mobile: "+91 99887 76655",
    ageGender: "28 Y / Male",
    procedure: "ACL Reconstruction",
    otRoom: "OT-01",
    surgeryDateTime: "29/09/2026\n02:00 PM",
    doctor: "Dr. Anand",
    doctorRole: "Orthopedic Surgeon",
    status: "Scheduled",
    avatar: "M",
    avatarBg: "#8B5CF6",
  },
  {
    id: 4,
    name: "Lalitha M",
    patientId: "DZX5641810",
    mobile: "+91 91234 56789",
    ageGender: "60 Y / Female",
    procedure: "Cataract Surgery",
    otRoom: "OT-03",
    surgeryDateTime: "29/09/2026\n03:30 PM",
    doctor: "Dr. Meera",
    doctorRole: "Ophthalmologist",
    status: "Completed",
    avatar: "L",
    avatarBg: "#22C55E",
  },
  {
    id: 5,
    name: "Kumaravel S",
    patientId: "DZX5641811",
    mobile: "+91 93456 78901",
    ageGender: "50 Y / Male",
    procedure: "Hernia Repair",
    otRoom: "OT-02",
    surgeryDateTime: "29/09/2026\n05:00 PM",
    doctor: "Dr. Karthik",
    doctorRole: "General Surgeon",
    status: "Scheduled",
    avatar: "K",
    avatarBg: "#F59E0B",
  },
];

const STATUS_STYLE = {
  Scheduled: "bg-blue-50 text-blue-600 border border-blue-200",
  "In Progress": "bg-amber-50 text-amber-700 border border-amber-200",
  Completed: "bg-green-50 text-green-700 border border-green-200",
  Cancelled: "bg-red-50 text-red-600 border border-red-200",
};

const STAT_CARDS = [
  {
    label: "Total Surgeries",
    value: "02",
    pct: "0%",
    icon: <TbChecklist className="text-[#EF4444] text-lg" />,
    iconBg: "#FEE2E2",
    chart: "red",
  },
  {
    label: "Scheduled",
    value: "01",
    pct: "0%",
    icon: <TbClockHour4 className="text-[#6366F1] text-lg" />,
    iconBg: "#EDE9FE",
    chart: "indigo",
  },
  {
    label: "In Progress",
    value: "00",
    pct: "0%",
    icon: <BsFillPlayCircleFill className="text-[#F59E0B] text-lg" />,
    iconBg: "#FEF3C7",
    chart: "amber",
  },
  {
    label: "Completed",
    value: "01",
    pct: "0%",
    icon: <TbCircleCheck className="text-[#22C55E] text-lg" />,
    iconBg: "#DCFCE7",
    chart: "green",
  },
];

const CHART_COLORS = {
  red: ["#FECACA", "#FCA5A5", "#F87171", "#EF4444", "#DC2626"],
  indigo: ["#C7D2FE", "#A5B4FC", "#818CF8", "#6366F1", "#4F46E5"],
  amber: ["#FDE68A", "#FCD34D", "#FBBF24", "#F59E0B", "#D97706"],
  green: ["#BBF7D0", "#86EFAC", "#4ADE80", "#22C55E", "#16A34A"],
};

const Sparkline = ({ color }) => (
  <div className="flex items-end gap-0.5 h-10">
    {CHART_COLORS[color].map((c, i) => (
      <div
        key={i}
        style={{ backgroundColor: c, height: `${(i + 1) * 20}%`, width: "6px", borderRadius: "2px" }}
      />
    ))}
  </div>
);

export default function OverTime() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [otRoomFilter, setOtRoomFilter] = useState("All");
  const [date, setDate] = useState("29/09/2026");

  const filtered = MOCK_PATIENTS.filter((p) => {
    const matchSearch =
      search === "" ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.patientId.toLowerCase().includes(search.toLowerCase()) ||
      p.mobile.includes(search);
    const matchStatus = statusFilter === "All" || p.status === statusFilter;
    const matchRoom = otRoomFilter === "All" || p.otRoom === otRoomFilter;
    return matchSearch && matchStatus && matchRoom;
  });

  return (
    <Layouts>
      <div className="p-4">
        {/* ── Header ── */}
        <div className="flex items-center gap-4 mb-6">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#F3E8FF" }}
          >
            <GiScalpel className="text-[#8B5CF6] text-2xl" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#333333] font-sans">OT</h1>
            <p className="text-sm text-gray-500 font-sans">
              Manage operation theatre patients and surgeries
            </p>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {STAT_CARDS.map((card) => (
            <div
              key={card.label}
              className="bg-white border rounded-xl shadow-sm p-4 flex items-center justify-between"
            >
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: card.iconBg }}
                  >
                    {card.icon}
                  </div>
                  <p className="text-xs font-sans text-gray-500">{card.label}</p>
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold text-[#333333] font-sans">{card.value}</p>
                  <span className="text-xs text-green-500 font-sans">↑ {card.pct}</span>
                </div>
              </div>
              <Sparkline color={card.chart} />
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        <div className="bg-white border rounded-xl shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 border rounded-lg px-3 py-2 flex-1 min-w-[200px]">
            <svg className="text-gray-400 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" strokeWidth="2" />
              <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by patient name, ID, mobile number..."
              className="text-sm font-sans outline-none flex-1 bg-transparent text-gray-700 placeholder-gray-400"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="flex flex-col">
              <label className="text-[10px] font-sans text-gray-400 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm font-sans text-gray-700 outline-none bg-white"
              >
                {["All", "Scheduled", "In Progress", "Completed", "Cancelled"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-[10px] font-sans text-gray-400 mb-1">OT Room</label>
              <select
                value={otRoomFilter}
                onChange={(e) => setOtRoomFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm font-sans text-gray-700 outline-none bg-white"
              >
                {["All", "OT-01", "OT-02", "OT-03"].map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-[10px] font-sans text-gray-400 mb-1">Date</label>
              <div className="border rounded-lg px-3 py-2 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2" strokeWidth="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="text-sm font-sans outline-none bg-transparent text-gray-700 w-24"
                />
              </div>
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={() => { setSearch(""); setStatusFilter("All"); setOtRoomFilter("All"); }}
                className="border rounded-lg p-2 text-gray-500 hover:bg-gray-50"
              >
                <FaSync className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setSearch(""); setStatusFilter("All"); setOtRoomFilter("All"); }}
                className="border rounded-lg px-4 py-2 text-sm font-sans text-[#189FAA] font-semibold hover:bg-teal-50"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-sans">
              <thead>
                <tr className="bg-gray-50 border-b">
                  {["#", "Patient", "Mobile Number", "Age / Gender", "Procedure", "OT Room", "Surgery Date & Time", "Surgeon", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-gray-400 text-sm">
                      No surgery records found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((p, idx) => (
                    <tr key={p.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ backgroundColor: p.avatarBg }}
                          >
                            {p.avatar}
                          </div>
                          <div>
                            <p className="font-semibold text-[#333333] text-xs">{p.name}</p>
                            <p className="text-[10px] text-gray-400">{p.patientId}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.mobile}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.ageGender}</td>
                      <td className="px-4 py-3 text-gray-600">{p.procedure}</td>
                      <td className="px-4 py-3 text-gray-600 font-medium">{p.otRoom}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-pre-line text-xs">{p.surgeryDateTime}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                            {p.doctor.charAt(3)}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-[#333333]">{p.doctor}</p>
                            <p className="text-[10px] text-gray-400">{p.doctorRole}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-md text-xs font-semibold ${STATUS_STYLE[p.status] || "bg-gray-100 text-gray-600"}`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button className="text-[#189FAA] hover:text-teal-700">
                          <FaEye className="text-base" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <p className="text-xs text-gray-400 font-sans">
              Showing 1 to {filtered.length} of {filtered.length} entries
            </p>
            <div className="flex items-center gap-2">
              <button className="px-3 py-1 border rounded-md text-xs text-gray-500 hover:bg-gray-50">‹</button>
              <button className="px-3 py-1 rounded-md text-xs bg-teal-500 text-white">1</button>
              <button className="px-3 py-1 border rounded-md text-xs text-gray-500 hover:bg-gray-50">›</button>
              <select className="border rounded-md px-2 py-1 text-xs text-gray-600 outline-none">
                <option>10 / page</option>
                <option>20 / page</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </Layouts>
  );
}
